import { Button, Checkbox, Column, Container, DatePricker, Label, Select, Switch, TextInput, Title } from 'components';
import { AutoCompliteMany } from 'components/AutoComplite/AutoCompliteMany';
import { MIN_TEXT_INPUT_LENGTH } from 'global-constants';
import { ExpertArea, UpdateExpertCertificate } from 'models/expert-certificates';
import { MIN_MONTH_ENABLE_CERTIFICATE } from 'pages/tests/test-form/constants';
import React, { FC, useEffect, useState } from 'react';
import { Controller, FieldValues, useForm } from 'react-hook-form-new';
import { useTranslation } from 'react-i18next';
import { AdminExpertCertificateService } from 'services/expert-certificate.service';

interface Props {
    data?: UpdateExpertCertificate;
    add: (val: UpdateExpertCertificate) => void;
    update: (val: UpdateExpertCertificate) => void;
    areas: ExpertArea[];
}

/*
ownerName: string;
    isDeleted: boolean;
    userId: string;
    expiredDate?: string | null;
    countOfMount?: number | null;
    expertAreaId: string;
*/

export const EditCertificate: FC<Props> = ({ data, add, update, areas }) => {
    const [t] = useTranslation(['validation', 'admin-validation']);
    const [useFinishDate, setUseFinishDate] = useState(true);
    const methods = useForm<UpdateExpertCertificate>({ defaultValues: data });
    const {
        register,
        control,
        setValue,
        getValues,
        formState: { isSubmitting, errors },
        handleSubmit,
    } = methods;

    useEffect(() => {
        if (useFinishDate) setValue('countOfMount', null);
        else setValue('expiredDate', undefined);
    }, [useFinishDate]);

    const getAreas = () => areas.map((v) => ({ label: v.name, value: v.id }));

    const submit = (fields: FieldValues) => {
        data?.id ? update(fields as UpdateExpertCertificate) : add(fields as UpdateExpertCertificate);
    };

    return (
        <Container>
            <Column columnSize="50">
                <Title titleText={data?.id ? 'Редактирование' : 'Добавление'} />
            </Column>

            <Column columnSize="100">
                <form onSubmit={handleSubmit(submit)} autoComplete="off">
                    <input type="hidden" {...register('id')} defaultValue={data?.id} />
                    <Column columnSize="100">
                        <Column columnSize="100">
                            <Controller
                                name="userId"
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t('admin-validation:required-select'),
                                    },
                                }}
                                render={({ field: { onChange } }) => (
                                    <AutoCompliteMany
                                        name="coachesId"
                                        additionalClasses="form-field"
                                        label="Пользователь"
                                        id="userId"
                                        labelField="name"
                                        valueField="id"
                                        isDisabled={!!data?.userId}
                                        values={data?.userId ? [{ id: data.userId, name: data.ownerName }] : []}
                                        onChange={(e) => {
                                            onChange(e ? e[0]?.id ?? '' : '');
                                            setValue('ownerName', e ? e[0]?.name ?? '' : '');
                                        }}
                                        getArrAsync={(s?: string) =>
                                            AdminExpertCertificateService.getUsers(s).then((d) =>
                                                d.data.map((m) => ({
                                                    id: m.id,
                                                    name: m.name,
                                                }))
                                            )
                                        }
                                        errorMessage={errors.userId?.message as string}
                                    />
                                )}
                            />
                        </Column>
                        <Column columnSize="100">
                            <TextInput
                                label="Имя"
                                id="nameId"
                                reg={register('ownerName', {
                                    required: {
                                        value: true,
                                        message: t('validation:required-field'),
                                    },
                                    minLength: {
                                        value: MIN_TEXT_INPUT_LENGTH,
                                        message: t('validation:min-length', {
                                            minLength: MIN_TEXT_INPUT_LENGTH,
                                        }),
                                    },
                                    maxLength: {
                                        value: 255,
                                        message: t('validation:max-length', {
                                            maxLength: 255,
                                        }),
                                    },
                                })}
                                errorMessage={errors.ownerName?.message as string}
                            />
                        </Column>
                        <Column columnSize="100" className="p-right-space-3x">
                            <Label labelText={'Область экспертизы'} labelFor="" />
                            <Controller
                                control={control}
                                name="expertAreaId"
                                rules={{
                                    required: {
                                        value: true,
                                        message: t('admin-validation:required-select'),
                                    },
                                }}
                                defaultValue={data?.expertAreaId || areas[0].id}
                                render={({ field: { onChange } }) => (
                                    <Select
                                        isDisabled={!!data?.expertAreaId}
                                        options={getAreas()}
                                        onChange={(v) => onChange(v[0]?.value)}
                                        values={[
                                            getAreas().find((r) => r.value === data?.expertAreaId) ?? getAreas()[0],
                                        ]}
                                        errorMessage={errors.expertAreaId?.message as string}
                                    />
                                )}
                            />
                        </Column>
                        <Column columnSize="100" className="p-right-space-3x top-space-10x">
                            <Controller
                                control={control}
                                name="isDeleted"
                                defaultValue={data?.isDeleted || false}
                                render={({ field: { onChange } }) => (
                                    <Switch
                                        label="Удалён"
                                        id="isDeleted"
                                        defaultChecked={data?.isDeleted || false}
                                        onChange={(e) => onChange(e)}
                                    />
                                )}
                            />
                        </Column>
                        <Column columnSize="100" className="p-right-space-3x top-space-10x">
                            <Checkbox
                                isChecked={useFinishDate}
                                label="Указать дату окончания действия сертификата"
                                handleChange={() => setUseFinishDate(!useFinishDate)}
                                id="finishDate"
                            />
                        </Column>
                        {!useFinishDate ? (
                            <Column columnSize="100" className="p-right-space-3x top-space-10x">
                                <TextInput
                                    type="number"
                                    defaultValue={1}
                                    label="Количество месяцев"
                                    id="certificationMonthCount"
                                    reg={register('countOfMount', {
                                        min: {
                                            value: MIN_MONTH_ENABLE_CERTIFICATE,
                                            message: t('incorrect-input'),
                                        },
                                    })}
                                    errorMessage={errors.countOfMount?.message as string}
                                />
                            </Column>
                        ) : (
                            <Container additionalClass="container--items-end">
                                <Column columnSize="100" className="p-right-space-3x top-space-10x">
                                    <Label labelText="Окончание действия сертификата" labelFor="" />
                                    <Controller
                                        name="expiredDate"
                                        defaultValue={data?.expiredDate}
                                        control={control}
                                        render={() => (
                                            <DatePricker
                                                canEmpty
                                                canDelete
                                                useZeroTime
                                                name="expiredDate"
                                                returnValue="end"
                                                value={getValues('expiredDate')}
                                                onChange={(v) => setValue('expiredDate', v as string)}
                                                errorMessage={errors.expiredDate?.message as string}
                                            />
                                        )}
                                    />
                                </Column>
                            </Container>
                        )}
                    </Column>

                    <Column columnSize="100">
                        <div className="info-buttons d-flex">
                            <Button type="submit" buttonClass="primary" isDisabled={isSubmitting}>
                                Сохранить
                            </Button>
                        </div>
                    </Column>
                </form>
            </Column>
        </Container>
    );
};
